/**
 * Tiny useQuery-ish hook. No react-query, no state library — the whole app is
 * read-mostly and talks to one local backend.
 *
 * Contract with the UI: `loading` is only true for the FIRST load (nothing to
 * paint yet). A refetch caused by a dependency change or polling keeps the old
 * data on screen and flips `refreshing` instead, so ranges/filters never blink.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import { JarvisApiError } from "../lib/api";
import { subscribeRefresh } from "../lib/refresh";

export interface ApiState<T> {
  data: T | null;
  /** Ukrainian, user-facing. Null when the last attempt succeeded. */
  error: string | null;
  /** Machine-readable slug: backend `code`, or "offline" / "bad_response" / "unknown". */
  code: string | null;
  loading: boolean;
  refreshing: boolean;
  /** Epoch ms of the last successful load, for "оновлено о ..." labels. */
  fetchedAt: number | null;
  /**
   * Epoch ms at which that successful load *left* — not when it landed.
   *
   * This is what an optimistic overlay needs and `fetchedAt` cannot give it.
   * A write that settled before this request went out is, by definition,
   * already reflected in the response; one that settled after it may not be.
   * Comparing against the arrival time instead would throw away local edits
   * made while the request was in flight (see `usePendingWrites`).
   */
  startedAt: number | null;
  refetch: () => void;
}

export interface UseApiOptions {
  /** Skip fetching entirely (e.g. a tab that is not open yet). */
  enabled?: boolean;
  /** Background poll interval in ms. Omit for one-shot. */
  pollMs?: number;
}

/**
 * Turn anything thrown by `lib/api.ts` into a message a human can act on.
 * The common real-world case is "the backend is not running", which surfaces
 * as a TypeError from fetch (or a non-JSON body through the Vite proxy).
 */
export function describeError(err: unknown): { message: string; code: string } {
  if (err instanceof JarvisApiError) {
    // `http_error` is the fallback code from lib/api.ts, meaning the body was
    // not a JSON ApiError — i.e. nobody on the other end spoke our protocol.
    // Combined with a 5xx that is the Vite proxy failing to reach the backend.
    const noApiBody = err.code === "http_error";
    if (err.status >= 500 && noApiBody) {
      return {
        message:
          "Бекенд Jarvis не відповідає. Запусти його командою «uv run jarvis» і натисни «Спробувати ще раз».",
        code: "backend_down",
      };
    }
    return { message: err.message, code: err.code || `http_${err.status}` };
  }
  if (err instanceof SyntaxError) {
    return {
      message: "Сервер повернув не JSON. Схоже, бекенд Jarvis не запущено.",
      code: "bad_response",
    };
  }
  if (err instanceof TypeError) {
    return {
      message: "Немає зв’язку з бекендом Jarvis. Запусти «uv run jarvis» і спробуй ще раз.",
      code: "offline",
    };
  }
  if (err instanceof Error) return { message: err.message, code: "unknown" };
  return { message: "Невідома помилка під час завантаження.", code: "unknown" };
}

/**
 * @param fetcher one of the helpers from `lib/api.ts`
 * @param deps    re-fetch when these change (same idea as useEffect deps)
 */
export function useApi<T>(
  fetcher: () => Promise<T>,
  deps: readonly unknown[] = [],
  options: UseApiOptions = {},
): ApiState<T> {
  const { enabled = true, pollMs } = options;

  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(enabled);
  const [refreshing, setRefreshing] = useState(false);
  const [fetchedAt, setFetchedAt] = useState<number | null>(null);
  const [startedAt, setStartedAt] = useState<number | null>(null);
  const [nonce, setNonce] = useState(0);

  // Keep the latest fetcher without making it a dependency: callers build it
  // inline (`() => getSpending(from, to)`), so it is a new function every render.
  const fetcherRef = useRef(fetcher);
  fetcherRef.current = fetcher;
  const hasData = useRef(false);

  useEffect(() => {
    if (!enabled) {
      setLoading(false);
      return;
    }
    let cancelled = false;

    const run = async (background: boolean): Promise<void> => {
      if (background || hasData.current) setRefreshing(true);
      else setLoading(true);
      const began = Date.now();
      try {
        const result = await fetcherRef.current();
        if (cancelled) return;
        setData(result);
        hasData.current = true;
        setError(null);
        setCode(null);
        setFetchedAt(Date.now());
        setStartedAt(began);
      } catch (err) {
        if (cancelled) return;
        const described = describeError(err);
        setError(described.message);
        setCode(described.code);
      } finally {
        if (!cancelled) {
          setLoading(false);
          setRefreshing(false);
        }
      }
    };

    void run(false);

    if (!pollMs) {
      return () => {
        cancelled = true;
      };
    }
    const timer = window.setInterval(() => void run(true), pollMs);
    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, pollMs, nonce, ...deps]);

  const refetch = useCallback(() => setNonce((n) => n + 1), []);

  // «Оновити» anywhere on the page (lib/refresh.ts) re-reads every mounted
  // query, including panels the page does not own — and because the request
  // goes out inside the refresh window, it goes past the server's cache.
  useEffect(() => (enabled ? subscribeRefresh(refetch) : undefined), [enabled, refetch]);

  return { data, error, code, loading, refreshing, fetchedAt, startedAt, refetch };
}

